/**
 * O orçamento aberto, com os passos do mockup aprovado (27/10/2026): (1) montar e enviar — produtos, texto do e-mail,
 * condições e envio; (2) a resposta do síndico; (3) a OS de Venda e o Compras. Cada mudança salva na hora e a tela mostra
 * o que o servidor devolveu (o número, a versão, a frase do erro).
 */
import { useCallback, useEffect, useState } from 'react';

import { Badge, Button, HelpTip, Input, Label, Select } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { parseNumeroBr, reaisStringToCentavos } from '@/lib/format';
import { cn } from '@/lib/cn';

import { abrirPdf, brl, quando, quantidadeBr } from './formato';
import { mensagemDoErro, orcamentoApi, ROTULO_DO_STATUS, type ConfigDoOrcamento, type DetalheDoOrcamento, type MudancaDoOrcamento, type ProdutoBuscado } from './orcamento-api';

const CLASSE_DO_GRUPO = 'rounded-lg border border-line bg-paper-surface p-4 dark:border-line-dark dark:bg-paper-dark-surface';
const CLASSE_DO_TITULO = 'text-xs font-bold uppercase tracking-wide text-ink-3 dark:text-ink-dark-3';

const TOM: Record<DetalheDoOrcamento['status'], 'neutral' | 'warning' | 'success' | 'danger' | 'info'> = {
  novo: 'neutral',
  enviado: 'warning',
  aprovado: 'success',
  local: 'success',
  contrato: 'success',
  compras: 'info',
  recusado: 'danger',
};

export function DetalheDoOrcamentoPainel({ id, aoMudar }: { id: string; aoMudar: (d: DetalheDoOrcamento) => void }) {
  const [d, setD] = useState<DetalheDoOrcamento | null>(null);
  const [cfg, setCfg] = useState<ConfigDoOrcamento | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const { show } = useToast();

  useEffect(() => {
    setD(null);
    setErro(null);
    orcamentoApi.detalhe(id).then(setD).catch((e) => setErro(mensagemDoErro(e, 'Não deu para abrir o orçamento.')));
    orcamentoApi.config().then(setCfg).catch(() => setCfg(null));
  }, [id]);

  /** Roda um gesto: mostra a frase do erro no lugar, ou o orçamento novo que o servidor devolveu. */
  const agir = useCallback(
    async (fn: () => Promise<DetalheDoOrcamento>, sucesso?: string) => {
      setOcupado(true);
      setErro(null);
      try {
        const novo = await fn();
        setD(novo);
        aoMudar(novo);
        if (sucesso) show(sucesso, { variant: 'success' });
      } catch (e) {
        setErro(mensagemDoErro(e));
      } finally {
        setOcupado(false);
      }
    },
    [aoMudar, show],
  );

  if (!d) {
    return erro ? (
      <p role="alert" className="text-sm text-red-deep dark:text-red-glow">
        {erro}
      </p>
    ) : (
      <p className="text-sm text-ink-3 dark:text-ink-dark-3">Abrindo o orçamento…</p>
    );
  }

  const editavel = d.status === 'novo' || d.status === 'enviado';
  const mudar = (m: MudancaDoOrcamento) => agir(() => orcamentoApi.mudar(d.id, m));

  return (
    <section aria-label="Orçamento aberto" className="flex flex-col gap-4" data-testid="orcamento-aberto">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <strong data-testid="orcamento-codigo">{d.codigo}</strong>
            <span className="text-ink-3 dark:text-ink-dark-3">· versão {d.versao}</span>
            <Badge variant={TOM[d.status]} size="sm" data-testid="orcamento-status">
              {ROTULO_DO_STATUS[d.status]}
            </Badge>
          </div>
          <h2 className="mt-1 font-display text-xl font-semibold text-ink-1 dark:text-ink-dark-1">{d.clienteNome}</h2>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            {d.origem === 'tecnico' ? 'Feito pelo técnico' : 'Feito pelo escritório'}
            {d.autorNome ? ` · ${d.autorNome}` : ''}
            {d.osNumero ? ` · OS #${d.osNumero}` : ''}
            {d.envio.vezes > 0 ? ` · enviado ${d.envio.vezes === 1 ? '1 vez' : `${d.envio.vezes} vezes`}, a última em ${quando(d.envio.ultimoEmIso)}` : ''}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void abrirPdf(d.id).catch(() => setErro('Não deu para abrir o PDF.'))} className="min-h-[44px] md:min-h-0">
          Ver o PDF
        </Button>
      </header>

      {d.mudouDepoisDeEnviar ? (
        <p className="rounded-lg border border-warning/40 bg-warning-tint px-3 py-2 text-sm text-ink-1" data-testid="orcamento-versao-nova">
          Versão {d.versao} ainda não enviada. Os links da versão {d.versaoEnviada} deixaram de valer.
        </p>
      ) : null}

      <Itens key={`itens-${d.id}`} d={d} cfg={cfg} editavel={editavel} agir={agir} />

      {editavel ? (
        <>
          <TextoDoEmail key={`texto-${d.id}`} d={d} mudar={mudar} />
          <Condicoes key={`cond-${d.id}`} d={d} cfg={cfg} mudar={mudar} />
          <Envio key={`envio-${d.id}`} d={d} ocupado={ocupado} agir={agir} />
        </>
      ) : null}

      {d.status === 'enviado' ? <Resposta key={`resp-${d.id}`} d={d} agir={agir} /> : null}
      {d.status === 'aprovado' || d.status === 'local' || d.status === 'contrato' ? <Compras key={`compras-${d.id}`} d={d} agir={agir} /> : null}
      {d.status === 'compras' ? (
        <p className="text-sm text-ink-2 dark:text-ink-dark-2" data-testid="orcamento-no-compras">
          No Compras com a OS de Venda <strong>{d.osVenda}</strong>.
        </p>
      ) : null}
      {d.status === 'recusado' ? (
        <p className="text-sm text-ink-2 dark:text-ink-dark-2">
          Recusado{d.resposta.porEmail ? ` por ${d.resposta.porEmail}` : d.resposta.porUsuario ? `, marcado por ${d.resposta.porUsuario}` : ''}
          {d.resposta.motivoDaRecusa ? `: ${d.resposta.motivoDaRecusa}` : '.'}
        </p>
      ) : null}

      {erro ? (
        <p role="alert" className="text-sm font-semibold text-red-deep dark:text-red-glow" data-testid="orcamento-erro">
          {erro}
        </p>
      ) : null}
    </section>
  );
}

type Agir = (fn: () => Promise<DetalheDoOrcamento>, sucesso?: string) => Promise<void>;

function Itens({ d, cfg, editavel, agir }: { d: DetalheDoOrcamento; cfg: ConfigDoOrcamento | null; editavel: boolean; agir: Agir }) {
  const [buscando, setBuscando] = useState(false);
  return (
    <div className={CLASSE_DO_GRUPO}>
      <div className="mb-2 flex items-center gap-1">
        <span className={CLASSE_DO_TITULO}>Produtos</span>
        <HelpTip texto="O custo é o do cadastro do Omie da empresa do orçamento. O preço é o custo mais o markup escolhido no catálogo." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 dark:text-ink-dark-3">
              <th className="py-1.5 pr-2">Produto</th>
              <th className="py-1.5 pr-2 text-right">Qtd</th>
              <th className="py-1.5 pr-2 text-right">Custo</th>
              <th className="py-1.5 pr-2 text-right">Markup</th>
              <th className="py-1.5 pr-2 text-right">Unitário</th>
              <th className="py-1.5 text-right">Total</th>
              {editavel ? <th className="w-10" aria-label="Tirar" /> : null}
            </tr>
          </thead>
          <tbody>
            {d.itens.map((i) => (
              <tr key={i.id} className="border-t border-line dark:border-line-dark" data-testid="orcamento-item">
                <td className="py-1.5 pr-2">{i.descricao}</td>
                <td className="py-1.5 pr-2 text-right">
                  {editavel ? (
                    <Input
                      aria-label={`Quantidade de ${i.descricao}`}
                      defaultValue={quantidadeBr(i.quantidade)}
                      inputMode="decimal"
                      className="!h-9 w-20 text-right"
                      onBlur={(e) => {
                        const q = parseNumeroBr(e.target.value);
                        if (q !== null && q !== i.quantidade) void agir(() => orcamentoApi.mudarItem(d.id, i.id, { quantidade: q }));
                      }}
                    />
                  ) : (
                    quantidadeBr(i.quantidade)
                  )}
                </td>
                <td className="py-1.5 pr-2 text-right text-ink-3 dark:text-ink-dark-3">{brl(i.custoUnitCentavos)}</td>
                <td className="py-1.5 pr-2 text-right">
                  {editavel && cfg ? (
                    <Select
                      aria-label={`Markup de ${i.descricao}`}
                      value={String(i.markupPct)}
                      onChange={(e) => void agir(() => orcamentoApi.mudarItem(d.id, i.id, { markupPct: Number(e.target.value) }))}
                      className="!h-9 w-24"
                    >
                      {cfg.markups.map((m) => (
                        <option key={m.id} value={String(m.percentual)}>
                          {m.percentual}%
                        </option>
                      ))}
                    </Select>
                  ) : (
                    `${i.markupPct}%`
                  )}
                </td>
                <td className="py-1.5 pr-2 text-right">{brl(i.precoUnitCentavos)}</td>
                <td className="py-1.5 text-right font-semibold">{brl(i.totalCentavos)}</td>
                {editavel ? (
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      aria-label={`Tirar ${i.descricao}`}
                      className="inline-flex h-11 w-11 items-center justify-center text-red-deep hover:text-red md:h-8 md:w-8"
                      onClick={() => void agir(() => orcamentoApi.removerItem(d.id, i.id))}
                    >
                      ×
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
            {d.mao.modo ? (
              <tr className="border-t border-line dark:border-line-dark">
                <td colSpan={5} className="py-1.5 pr-2">
                  Mão de obra ({quantidadeBr(d.mao.quantidade)} {d.mao.modo === 'diaria' ? (d.mao.quantidade === 1 ? 'diária' : 'diárias') : 'h'})
                </td>
                <td className="py-1.5 text-right font-semibold">{brl(d.conta.maoCentavos)}</td>
                {editavel ? <td /> : null}
              </tr>
            ) : null}
            {d.conta.descontoCentavos > 0 ? (
              <tr className="border-t border-line dark:border-line-dark">
                <td colSpan={5} className="py-1.5 pr-2">
                  Desconto{d.desconto.modo === 'pct' ? ` (${d.desconto.pct}%)` : ''}
                </td>
                <td className="py-1.5 text-right">− {brl(d.conta.descontoCentavos)}</td>
                {editavel ? <td /> : null}
              </tr>
            ) : null}
            <tr className="border-t-2 border-ink-1 dark:border-ink-dark-1">
              <td colSpan={5} className="py-2 pr-2 font-bold">
                Total
              </td>
              <td className="py-2 text-right font-display text-lg font-semibold" data-testid="orcamento-total">
                {brl(d.conta.totalCentavos)}
              </td>
              {editavel ? <td /> : null}
            </tr>
          </tbody>
        </table>
      </div>
      {d.itens.length === 0 ? <p className="mt-2 text-sm text-ink-3 dark:text-ink-dark-3">Nenhum produto ainda.</p> : null}
      {editavel ? (
        buscando ? (
          <BuscaDeProduto
            aoEscolher={(p, quantidade) => {
              setBuscando(false);
              void agir(() => orcamentoApi.acrescentarItem(d.id, { codigoProduto: p.codigoProduto, quantidade }));
            }}
            aoFechar={() => setBuscando(false)}
          />
        ) : (
          <Button variant="secondary" size="sm" className="mt-3 min-h-[44px] md:min-h-0" onClick={() => setBuscando(true)} data-testid="orcamento-adicionar-produto">
            Adicionar produto
          </Button>
        )
      ) : null}
    </div>
  );
}

function BuscaDeProduto({ aoEscolher, aoFechar }: { aoEscolher: (p: ProdutoBuscado, quantidade: number) => void; aoFechar: () => void }) {
  const [q, setQ] = useState('');
  const [qtd, setQtd] = useState('1');
  const [achados, setAchados] = useState<{ items: ProdutoBuscado[]; total: number } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setAchados(null);
      return;
    }
    const t = setTimeout(() => {
      orcamentoApi.produtos(q.trim()).then(setAchados).catch(() => setAchados({ items: [], total: 0 }));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-lg border border-line p-3 dark:border-line-dark" data-testid="orcamento-busca-produto">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_96px]">
        <div>
          <Label htmlFor="orcamento-busca">Produto</Label>
          <Input id="orcamento-busca" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome ou código do produto" />
        </div>
        <div>
          <Label htmlFor="orcamento-qtd">Quantidade</Label>
          <Input id="orcamento-qtd" value={qtd} onChange={(e) => setQtd(e.target.value)} inputMode="decimal" />
        </div>
      </div>
      {achados && achados.items.length === 0 ? <p className="text-sm text-ink-3 dark:text-ink-dark-3">Nenhum produto com &quot;{q.trim()}&quot; no cadastro da empresa.</p> : null}
      {achados && achados.items.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {achados.items.map((p) => (
            <li key={p.codigoProduto}>
              <button
                type="button"
                className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md px-2 text-left text-sm hover:bg-paper dark:hover:bg-paper-dark"
                onClick={() => {
                  const n = parseNumeroBr(qtd);
                  if (n === null || n <= 0) return setErro('A quantidade precisa ser maior que zero.');
                  aoEscolher(p, n);
                }}
              >
                <span>
                  {p.descricao}
                  {p.codigo ? <span className="text-ink-3 dark:text-ink-dark-3"> · {p.codigo}</span> : null}
                </span>
                <span className="text-ink-3 dark:text-ink-dark-3">custo {brl(p.custoUnitarioCentavos)}</span>
              </button>
            </li>
          ))}
          {achados.total > achados.items.length ? <li className="text-xs text-ink-3 dark:text-ink-dark-3">Mais {achados.total - achados.items.length}: escreva mais do nome para achar.</li> : null}
        </ul>
      ) : null}
      {erro ? (
        <p role="alert" className="text-sm text-red-deep dark:text-red-glow">
          {erro}
        </p>
      ) : null}
      <div>
        <Button variant="ghost" size="sm" onClick={aoFechar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function TextoDoEmail({ d, mudar }: { d: DetalheDoOrcamento; mudar: (m: MudancaDoOrcamento) => Promise<void> }) {
  return (
    <div className={CLASSE_DO_GRUPO}>
      <div className="mb-2 flex items-center gap-1">
        <span className={CLASSE_DO_TITULO}>Texto do e-mail</span>
        <HelpTip texto="O título vai no assunto do e-mail, junto com o nome do cliente. O motivo explica, numa frase, por que o orçamento existe." />
      </div>
      <div className="flex flex-col gap-3">
        <div>
          <Label htmlFor="orcamento-titulo">Título</Label>
          <Input
            id="orcamento-titulo"
            defaultValue={d.titulo}
            maxLength={140}
            onBlur={(e) => e.target.value.trim() !== d.titulo && void mudar({ titulo: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="orcamento-motivo">Motivo</Label>
          <textarea
            id="orcamento-motivo"
            rows={2}
            defaultValue={d.motivo}
            maxLength={2000}
            onBlur={(e) => e.target.value.trim() !== d.motivo && void mudar({ motivo: e.target.value })}
            className="w-full rounded-xl border border-line bg-paper-surface px-4 py-2 text-sm text-ink-1 focus:border-red focus:outline-none focus:ring-2 focus:ring-red-soft dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1"
          />
        </div>
      </div>
    </div>
  );
}

function Segmentos<T extends string | null>({ rotulo, opcoes, valor, aoEscolher }: { rotulo: string; opcoes: Array<[T, string]>; valor: T; aoEscolher: (v: T) => void }) {
  return (
    <div role="group" aria-label={rotulo} className="inline-flex flex-wrap gap-1">
      {opcoes.map(([v, nome]) => (
        <button
          key={String(v)}
          type="button"
          aria-pressed={valor === v}
          onClick={() => aoEscolher(v)}
          className={cn(
            'min-h-[44px] rounded-md border px-3 text-sm font-medium md:min-h-9',
            valor === v ? 'border-ink-1 bg-ink-1 text-paper dark:border-ink-dark-1 dark:bg-ink-dark-1 dark:text-paper-dark' : 'border-line text-ink-2 dark:border-line-dark dark:text-ink-dark-2',
          )}
        >
          {nome}
        </button>
      ))}
    </div>
  );
}

function Condicoes({ d, cfg, mudar }: { d: DetalheDoOrcamento; cfg: ConfigDoOrcamento | null; mudar: (m: MudancaDoOrcamento) => Promise<void> }) {
  const prazos = cfg?.prazos.filter((p) => p.ativo) ?? [];
  const descontos = cfg?.descontos.filter((x) => x.ativo) ?? [];
  return (
    <div className={CLASSE_DO_GRUPO}>
      <div className="mb-3 flex items-center gap-1">
        <span className={CLASSE_DO_TITULO}>Condições</span>
        <HelpTip texto="A forma de pagamento só mostra as que valem para o total deste orçamento. O desconto vale sobre os produtos e a mão de obra juntos." />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="orcamento-prazo">Prazo de entrega</Label>
          <Select id="orcamento-prazo" value={d.prazoEntrega ?? ''} onChange={(e) => void mudar({ prazoEntrega: e.target.value || null })}>
            <option value="">Escolha o prazo</option>
            {prazos.map((p) => (
              <option key={p.id} value={p.texto}>
                {p.texto}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="orcamento-pagamento">Forma de pagamento</Label>
          <Select id="orcamento-pagamento" value={d.pagamento ?? ''} onChange={(e) => void mudar({ pagamento: e.target.value || null })}>
            <option value="">Escolha a forma</option>
            {d.pagamentosQueValem.map((nome) => (
              <option key={nome} value={nome}>
                {nome}
              </option>
            ))}
            {d.pagamento && !d.pagamentosQueValem.includes(d.pagamento) ? <option value={d.pagamento}>{d.pagamento} (não vale para este total)</option> : null}
          </Select>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">Desconto</span>
          <Segmentos
            rotulo="Tipo de desconto"
            opcoes={[
              [null, 'Sem'],
              ['pct', 'Do catálogo'],
              ['valor', 'Valor fixo'],
            ]}
            valor={d.desconto.modo}
            aoEscolher={(v) => void mudar(v === null ? { descontoModo: null } : v === 'pct' ? { descontoModo: 'pct', descontoCentavos: 0 } : { descontoModo: 'valor', descontoPct: 0 })}
          />
          {d.desconto.modo === 'pct' ? (
            <Select aria-label="Desconto do catálogo" value={String(d.desconto.pct)} onChange={(e) => void mudar({ descontoModo: 'pct', descontoPct: Number(e.target.value) })}>
              <option value="0">Escolha o desconto</option>
              {descontos.map((x) => (
                <option key={x.id} value={String(x.percentual)}>
                  {x.percentual}%
                </option>
              ))}
            </Select>
          ) : null}
          {d.desconto.modo === 'valor' ? (
            <Input
              aria-label="Desconto em reais"
              inputMode="decimal"
              placeholder="R$ 0,00"
              defaultValue={d.desconto.centavos ? (d.desconto.centavos / 100).toFixed(2).replace('.', ',') : ''}
              onBlur={(e) => void mudar({ descontoModo: 'valor', descontoCentavos: reaisStringToCentavos(e.target.value) })}
            />
          ) : null}
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">Mão de obra</span>
          <Segmentos
            rotulo="Mão de obra"
            opcoes={[
              [null, 'Sem'],
              ['diaria', 'Por diária'],
              ['hora', 'Por hora'],
            ]}
            valor={d.mao.modo}
            aoEscolher={(v) => void mudar({ maoModo: v })}
          />
          {d.mao.modo ? (
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor="orcamento-mao-qtd">{d.mao.modo === 'diaria' ? 'Dias' : 'Horas'}</Label>
                <Input
                  id="orcamento-mao-qtd"
                  key={`${d.mao.modo}-${d.mao.quantidade}`}
                  inputMode="decimal"
                  className="w-24"
                  defaultValue={quantidadeBr(d.mao.quantidade)}
                  onBlur={(e) => {
                    const q = parseNumeroBr(e.target.value);
                    if (q !== null && q !== d.mao.quantidade) void mudar({ maoQtd: q });
                  }}
                />
              </div>
              <span className="pb-2 text-xs text-ink-3 dark:text-ink-dark-3">
                {quantidadeBr(d.mao.quantidade)} × {brl(d.mao.baseCentavos)} + {d.mao.markupPct}% = {brl(d.conta.maoCentavos)}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Envio({ d, ocupado, agir }: { d: DetalheDoOrcamento; ocupado: boolean; agir: Agir }) {
  const [desmarcados, setDesmarcados] = useState<Set<string>>(new Set());
  const escolhidos = d.destinos.filter((x) => !desmarcados.has(x.email)).map((x) => x.email);
  const reenvio = d.envio.vezes > 0;
  return (
    <div className={CLASSE_DO_GRUPO}>
      <div className="mb-2 flex items-center gap-1">
        <span className={CLASSE_DO_TITULO}>Envio</span>
        <HelpTip texto='O e-mail vai aos e-mails do cliente marcados "Recebe orçamento", cada um com o seu link. Quem tem "Pode aprovar" recebe os botões Aprovar e Recusar. O PDF vai anexo.' />
      </div>
      {d.destinos.length === 0 ? (
        <p className="text-sm text-ink-2 dark:text-ink-dark-2" data-testid="orcamento-sem-destinos">
          Ninguém deste cliente recebe orçamento. Marque &quot;Recebe orçamento&quot; nos e-mails da ficha do cliente.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {d.destinos.map((x) => (
            <label key={x.email} className="flex min-h-[44px] items-center gap-2 text-sm md:min-h-0">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={!desmarcados.has(x.email)}
                onChange={(e) =>
                  setDesmarcados((s) => {
                    const n = new Set(s);
                    if (e.target.checked) n.delete(x.email);
                    else n.add(x.email);
                    return n;
                  })
                }
              />
              <span className="break-all">{x.email}</span>
              <span className="text-xs text-ink-3 dark:text-ink-dark-3">{x.podeAprovar ? 'com os botões' : 'sem os botões'}</span>
            </label>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          onClick={() =>
            void agir(
              async () => (await orcamentoApi.enviar(d.id, desmarcados.size ? escolhidos : undefined)).detalhe,
              reenvio ? 'E-mail reenviado.' : 'Orçamento enviado ao síndico.',
            )
          }
          disabled={ocupado || escolhidos.length === 0}
          loading={ocupado}
          className="min-h-[44px] md:min-h-0"
          data-testid="orcamento-enviar"
        >
          {reenvio ? (d.mudouDepoisDeEnviar ? `Enviar a versão ${d.versao}` : 'Reenviar o e-mail') : 'Enviar ao síndico'}
        </Button>
      </div>
    </div>
  );
}

function Resposta({ d, agir }: { d: DetalheDoOrcamento; agir: Agir }) {
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const comBotoes = d.destinos.filter((x) => x.podeAprovar).map((x) => x.email);
  return (
    <div className={CLASSE_DO_GRUPO} data-testid="orcamento-resposta">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">O síndico respondeu por telefone ou WhatsApp?</span>
        <HelpTip texto="Quem tem os botões no e-mail responde sozinho, e o orçamento muda de situação. Use estes botões quando a resposta chegou por outro caminho." />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="min-h-[44px] md:min-h-0" onClick={() => void agir(() => orcamentoApi.resposta(d.id, 'aprovado'), 'Marcado como aprovado.')}>
          Aprovado
        </Button>
        <Button size="sm" variant="secondary" className="min-h-[44px] md:min-h-0" onClick={() => setRecusando(true)}>
          Recusado
        </Button>
      </div>
      {recusando ? (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <Label htmlFor="orcamento-motivo-recusa">Motivo da recusa</Label>
            <Input id="orcamento-motivo-recusa" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: o condomínio vai esperar a assembleia" />
          </div>
          <Button size="sm" className="min-h-[44px] md:min-h-0" onClick={() => void agir(() => orcamentoApi.resposta(d.id, 'recusado', motivo), 'Marcado como recusado.')}>
            Confirmar a recusa
          </Button>
        </div>
      ) : null}
      <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
        {comBotoes.length ? `Com os botões no e-mail: ${comBotoes.join(', ')}.` : 'Ninguém recebeu os botões: a resposta é marcada aqui.'}
      </p>
    </div>
  );
}

function Compras({ d, agir }: { d: DetalheDoOrcamento; agir: Agir }) {
  const [osVenda, setOsVenda] = useState(d.osVenda ?? '');
  const [motivo, setMotivo] = useState('');
  const pelaPagina = d.status === 'aprovado' && !!d.resposta.porEmail;
  return (
    <div className={CLASSE_DO_GRUPO} data-testid="orcamento-compras">
      <p className="mb-3 text-sm">
        {d.status === 'local' ? 'Aprovado no atendimento.' : d.status === 'contrato' ? 'Aprovado pelo contrato.' : 'Aprovado'}
        {d.resposta.porEmail ? ` por ${d.resposta.porEmail}, pelo e-mail,` : d.resposta.porUsuario ? `, marcado por ${d.resposta.porUsuario},` : ''}
        {d.resposta.emIso ? ` em ${quando(d.resposta.emIso)}.` : ''} Falta a OS de Venda para mandar ao Compras.
      </p>
      {pelaPagina ? (
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <Label htmlFor="orcamento-motivo-desfazer">Desfazer esta aprovação: por quê?</Label>
            <Input id="orcamento-motivo-desfazer" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: o síndico ligou dizendo que não aprovou" />
          </div>
          <Button size="sm" variant="secondary" className="min-h-[44px] md:min-h-0" onClick={() => void agir(() => orcamentoApi.desfazer(d.id, motivo), 'Aprovação desfeita.')}>
            Desfazer a aprovação
          </Button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label htmlFor="orcamento-os-venda">OS de Venda (nº do pedido no Omie)</Label>
          <Input id="orcamento-os-venda" value={osVenda} onChange={(e) => setOsVenda(e.target.value)} inputMode="numeric" placeholder="Ex.: 721" className="w-40" />
        </div>
        <Button
          size="sm"
          className="min-h-[44px] md:min-h-0"
          data-testid="orcamento-enviar-compras"
          onClick={() => void agir(() => orcamentoApi.compras(d.id, osVenda), d.status === 'local' ? 'OS de Venda gravada.' : 'Enviado ao Compras.')}
        >
          {d.status === 'local' ? 'Gravar a OS de Venda' : 'Enviar ao Compras'}
        </Button>
      </div>
    </div>
  );
}
